import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

interface SEOMetaProps {
  title?: string;
  description?: string;
  keywords?: string;
  canonicalUrl?: string;
  ogImage?: string;
  ogType?: string;
  schemaType?: 'LocalBusiness' | 'Service' | 'WebPage' | 'FAQ';
  schemaData?: any;
}

const defaultSEO = {
  title: "Blue Knight Contracting & Construction - Ontario General Contractor | Construction Services",
  description: "Blue Knight Contracting and Construction is Ontario's trusted general contractor providing residential, commercial & industrial construction services in Toronto, Ottawa, Mississauga, Hamilton & across Ontario.",
  keywords: "Ontario general contractor, construction services Ontario, Toronto contractor, Ottawa construction, Mississauga contracting, Hamilton construction company, residential construction, commercial contracting, industrial construction, skilled trades Ontario",
  ogImage: "https://lovable.dev/opengraph-image-p98pqg.png",
  ogType: "website"
};

const SEOMeta: React.FC<SEOMetaProps> = ({
  title,
  description,
  keywords,
  canonicalUrl,
  ogImage,
  ogType = 'website',
  schemaType,
  schemaData
}) => {
  const location = useLocation();
  const currentUrl = `${window.location.origin}${location.pathname}`;
  
  const finalTitle = title || defaultSEO.title;
  const finalDescription = description || defaultSEO.description;
  const finalKeywords = keywords || defaultSEO.keywords;
  const finalCanonicalUrl = canonicalUrl || currentUrl;
  const finalOgImage = ogImage || defaultSEO.ogImage;

  useEffect(() => {
    // Update title
    document.title = finalTitle;

    // Update or create meta tags
    const updateMetaTag = (name: string, content: string, property = false) => {
      const selector = property ? `meta[property="${name}"]` : `meta[name="${name}"]`;
      let element = document.querySelector(selector) as HTMLMetaElement;
      
      if (!element) {
        element = document.createElement('meta');
        if (property) {
          element.setAttribute('property', name);
        } else {
          element.setAttribute('name', name);
        }
        document.head.appendChild(element);
      }
      element.setAttribute('content', content);
    };

    // Update or create link tags
    const updateLinkTag = (rel: string, href: string) => {
      let element = document.querySelector(`link[rel="${rel}"]`) as HTMLLinkElement;
      
      if (!element) {
        element = document.createElement('link');
        element.setAttribute('rel', rel);
        document.head.appendChild(element);
      }
      element.setAttribute('href', href);
    };

    // Basic meta tags
    updateMetaTag('description', finalDescription);
    updateMetaTag('keywords', finalKeywords);

    // Open Graph tags
    updateMetaTag('og:title', finalTitle, true);
    updateMetaTag('og:description', finalDescription, true);
    updateMetaTag('og:type', ogType, true);
    updateMetaTag('og:url', finalCanonicalUrl, true);
    updateMetaTag('og:image', finalOgImage, true);

    // Twitter Card tags
    updateMetaTag('twitter:card', 'summary_large_image');
    updateMetaTag('twitter:title', finalTitle);
    updateMetaTag('twitter:description', finalDescription);
    updateMetaTag('twitter:image', finalOgImage);

    // Canonical URL
    updateLinkTag('canonical', finalCanonicalUrl);

    // Schema markup
    if (schemaType && schemaData) {
      const existingSchema = document.querySelector('script[type="application/ld+json"]');
      if (existingSchema) {
        existingSchema.remove();
      }

      const script = document.createElement('script');
      script.type = 'application/ld+json';
      
      let schema;
      switch (schemaType) {
        case 'LocalBusiness':
          schema = {
            "@context": "https://schema.org",
            "@type": "GeneralContractor",
            "name": "Blue Knight Contracting & Construction",
            "description": finalDescription,
            "url": finalCanonicalUrl,
            "logo": finalOgImage,
            "image": finalOgImage,
            "telephone": "(416) 555-BLUE",
            "email": "info@blueknightcontracting.ca",
            "address": {
              "@type": "PostalAddress",
              "addressRegion": "Ontario",
              "addressCountry": "CA"
            },
            "areaServed": [
              {
                "@type": "City",
                "name": "Toronto",
                "addressRegion": "Ontario",
                "addressCountry": "CA"
              },
              {
                "@type": "City", 
                "name": "Ottawa",
                "addressRegion": "Ontario",
                "addressCountry": "CA"
              },
              {
                "@type": "City",
                "name": "Mississauga", 
                "addressRegion": "Ontario",
                "addressCountry": "CA"
              },
              {
                "@type": "City",
                "name": "Hamilton",
                "addressRegion": "Ontario", 
                "addressCountry": "CA"
              },
              {
                "@type": "City",
                "name": "London",
                "addressRegion": "Ontario",
                "addressCountry": "CA"
              },
              {
                "@type": "City",
                "name": "Kitchener",
                "addressRegion": "Ontario",
                "addressCountry": "CA" 
              }
            ],
            "serviceType": [
              "General Contracting",
              "Residential Construction", 
              "Commercial Construction",
              "Industrial Construction",
              "Skilled Trades",
              "Heavy Equipment Services",
              "Specialty Contracting",
              "Design Engineering"
            ],
            "hasCredential": [
              {
                "@type": "EducationalOccupationalCredential",
                "name": "Licensed General Contractor"
              },
              {
                "@type": "EducationalOccupationalCredential", 
                "name": "Fully Insured"
              }
            ],
            ...schemaData
          };
          break;
        case 'Service':
          schema = {
            "@context": "https://schema.org",
            "@type": "Service", 
            "serviceType": schemaData.serviceType,
            "provider": {
              "@type": "GeneralContractor",
              "name": "Blue Knight Contracting & Construction",
              "telephone": "(416) 555-BLUE",
              "email": "info@blueknightcontracting.ca"
            },
            "areaServed": {
              "@type": "State",
              "name": "Ontario"
            },
            ...schemaData
          };
          break;
        case 'WebPage':
          schema = {
            "@context": "https://schema.org",
            "@type": "WebPage",
            "name": finalTitle,
            "description": finalDescription,
            "url": finalCanonicalUrl,
            "isPartOf": {
              "@type": "WebSite",
              "name": "Blue Knight Contracting & Construction",
              "url": window.location.origin
            },
            ...schemaData
          };
          break;
        case 'FAQ':
          schema = {
            "@context": "https://schema.org",
            "@type": "FAQPage",
            "mainEntity": schemaData.questions || []
          };
          break;
        default:
          schema = schemaData;
      }

      script.textContent = JSON.stringify(schema);
      document.head.appendChild(script);
    }

    // Cleanup function
    return () => {
      // Remove schema when component unmounts
      const schemaScript = document.querySelector('script[type="application/ld+json"]');
      if (schemaScript) {
        schemaScript.remove();
      }
    };
  }, [finalTitle, finalDescription, finalKeywords, finalCanonicalUrl, finalOgImage, ogType, schemaType, schemaData]);

  return null; // This component doesn't render anything
};

export default SEOMeta;